/**
 * Life4Earth V2 — RTL Helpers
 *
 * Utility functions for Right-to-Left layout support.
 * Used primarily for inline styles in TSX files where CSS
 * logical properties can't be applied.
 */

/** RTL language codes — Arabic, Hebrew, Persian, Urdu */
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'] as const;

/** Check if a language code is RTL. */
export function isRtlLanguage(lang: string): boolean {
  const base = lang.split('-')[0].toLowerCase();
  return (RTL_LANGUAGES as readonly string[]).includes(base);
}

/** Check if the document is currently in RTL mode. */
export function isRtl(): boolean {
  return document.documentElement.dir === 'rtl';
}

/**
 * Convert Western (0-9) digits to Arabic-Indic (٠-٩) numerals.
 * When the document is in RTL mode (Arabic), numbers render in the
 * script readers expect.  In LTR mode the input is returned unchanged.
 *
 * Accepts numbers or strings.  Non-digit characters pass through.
 */
export function localizeNumber(value: number | string): string {
  const s = String(value);
  if (!isRtl()) return s;
  // Arabic-Indic digits start at U+0660
  return s.replace(/\d/g, (d) => String.fromCharCode(0x0660 + Number(d)));
}

/**
 * Returns `{ marginInlineStart: value }` using the physical property
 * that matches the current direction.
 *
 * In LTR → marginLeft; in RTL → marginRight.
 */
export function marginInlineStart(value: number | string): React.CSSProperties {
  return isRtl() ? { marginRight: value } : { marginLeft: value };
}

/**
 * Returns `{ marginInlineEnd: value }` using the physical property
 * that matches the current direction.
 */
export function marginInlineEnd(value: number | string): React.CSSProperties {
  return isRtl() ? { marginLeft: value } : { marginRight: value };
}

/**
 * Returns `{ paddingInlineStart: value }` using the physical property.
 */
export function paddingInlineStart(value: number | string): React.CSSProperties {
  return isRtl() ? { paddingRight: value } : { paddingLeft: value };
}

/**
 * Returns `{ paddingInlineEnd: value }` using the physical property.
 */
export function paddingInlineEnd(value: number | string): React.CSSProperties {
  return isRtl() ? { paddingLeft: value } : { paddingRight: value };
}

/**
 * Returns `{ textAlign: 'start' }` in physical form.
 */
export function textAlignStart(): React.CSSProperties {
  return { textAlign: isRtl() ? 'right' : 'left' };
}

/**
 * Returns `{ textAlign: 'end' }` in physical form.
 */
export function textAlignEnd(): React.CSSProperties {
  return { textAlign: isRtl() ? 'left' : 'right' };
}
